import { Link } from "@tanstack/react-router";
import {
  ArrowRightIcon,
  ArrowUpIcon,
  MicIcon,
  MicOffIcon,
  PhoneIcon,
  PhoneOffIcon,
  RotateCcwIcon,
  TargetIcon,
  TriangleAlertIcon,
  XIcon,
} from "lucide-react";
import type { ProbeBriefing, ProbeExaminationArea, ProbeMode, ProbeVoice } from "@amoreg/contracts";
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from "react";

import { RESTRICTED_PROVIDERS_COPY } from "../../regulation/probe/probeRefusal";
import type { ProbeCall } from "../../regulation/probe/useProbeCall";
import ChatMarkdown from "../../ChatMarkdown";
import FluidOrb from "../../ui/fluid-orb";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Sheet, SheetPopup } from "../../ui/sheet";
import { Tooltip, TooltipPopup, TooltipTrigger } from "../../ui/tooltip";
import {
  Select,
  SelectGroup,
  SelectGroupLabel,
  SelectItem,
  SelectPopup,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import type { ProbeInterviewSummary } from "../../../state/probeHistory";
import {
  describeProbeNotice,
  describeProbePhase,
  describeToolName,
  groupTranscript,
  type ProbeConversationState,
} from "../../../state/probeSession";
import { cn } from "~/lib/utils";
import { areaLabel } from "@amoreg/shared/probePractice";
import { focusAsk } from "./probeArea";
import { ProbeAreaProvider, useProbeArea } from "./ProbeAreaProvider";
import { OpenRowsProvider } from "./desk/DeskParts";
import type { DeskRecord } from "./desk/deskRecords";
import { hierarchyStageDesk } from "./desk/HierarchyDesk";
import { lineRecordsOf } from "./desk/lineRecords";
import { DESK_LABEL_CLASS, ProbeDeskButton, type ProbeStageDesk } from "./ProbeDesk";
import { liveDeskCall } from "./ProbeDesk.logic";
import { ProbeRecordChips } from "./ProbeRecordChips";
import { OutcomeBadge } from "./ProbeParts";
import { ProbeVoicePicker } from "./ProbeVoicePicker";

/**
 * Probe — a rehearsal for the regulator's visit.
 *
 * Probe is the examiner from the organization's own supervisory authority,
 * running the interview before the real one: it asks, you answer, it
 * follows up, and between questions it reads the organization's records
 * (policies, findings, regulator changes, the rulebook) to ask sharper ones
 * and to catch answers that contradict what is on file. It is not the
 * Inspector — it audits nothing and raises nothing.
 *
 * The page is the whole content area: the orb at the top is the examiner
 * (it beats while the examiner speaks, and the line under it says what it is
 * doing — listening, checking the policy library, speaking); the
 * conversation scrolls beneath in the chat's own message styling, with the
 * examiner's "mm-hmm"s riding on the answers they were said over; the
 * prompt box at the bottom is for the answers you would rather type. Once
 * the call connects, the examiner's file sits beside the conversation
 * (`ProbeDesk`), or, when the stage is narrower than 56rem, in a sheet behind
 * the Examiner's file button. The call runs browser ↔ OpenAI GPT-Live over
 * WebRTC; the lookups the examiner delegates run on our server as the
 * signed-in person (`useProbeCall`).
 *
 * The orb wears the theme's primary colour, and the voice is the person's
 * choice among GPT-Live's English voices — kept in local storage, applied
 * from the next call (a live session cannot change voice).
 */

const ORB_SIZE = 168;
const DOCKED_ORB_SIZE = 104;

/**
 * Probe outside its area's routes (an old `/$org/apps/$appId` link): the
 * stage with its own provider. The area's routes share one provider
 * instead, so a call outlives a visit to the briefing.
 */
export function ProbePage({ orgSegment }: { readonly orgSegment: string }) {
  return (
    <ProbeAreaProvider orgSegment={orgSegment}>
      <ProbeStagePage focus={null} onFocusChange={() => undefined} />
    </ProbeAreaProvider>
  );
}

/**
 * The stage: start an interview, and hold it. `focus` is an examination
 * area picked from Practice or the briefing; Start then opens the call
 * with the person asking for that area, as they could by voice.
 */
export function ProbeStagePage({
  focus,
  onFocusChange,
  deskOf = hierarchyStageDesk,
}: {
  readonly focus: ProbeExaminationArea | null;
  readonly onFocusChange: (focus: ProbeExaminationArea | null) => void;
  /** The Probe desk lab (`apps/web/e2e/probeDeskLab`) draws its desk variants here. */
  readonly deskOf?: ProbeStageDesk;
}) {
  const area = useProbeArea();
  const {
    call,
    briefing,
    voice,
    setVoice,
    mode,
    setMode,
    organizationId,
    orgLoading,
    orgName,
    lastSaved,
  } = area;
  const { state } = call;
  const busy = state.phase === "connecting";
  const unconfigured = briefing?.configured === false;
  const restricted = area.briefingStatus === "restricted";
  const canAsk = organizationId !== null && !busy && !unconfigured && !restricted;

  const begin = () => {
    if (focus !== null) {
      // The focus is spent once asked for; the transcript shows the request.
      call.ask(focusAsk(focus));
      onFocusChange(null);
      return;
    }
    void call.start();
  };

  const hasTranscript = state.transcript.length > 0;
  const deskShown = state.sessionId !== null;
  const sheetHeadingId = useId();
  const deskCall = useMemo(
    () =>
      liveDeskCall({ tools: state.tools, records: state.records, transcript: state.transcript }),
    [state.tools, state.records, state.transcript],
  );
  const desk = useMemo(() => deskOf(deskCall, briefing), [deskOf, deskCall, briefing]);
  const lineRecords = useMemo(() => lineRecordsOf(deskCall, briefing), [deskCall, briefing]);
  const [sheetFor, setSheetFor] = useState<string | null>(null);
  const column = useRef<HTMLElement>(null);
  const sessionId = state.sessionId;
  /** A chip's record is on the desk: in the column, or below its width in the sheet. */
  const showDesk = useCallback(() => {
    if (column.current?.checkVisibility?.() !== true) setSheetFor(sessionId);
  }, [sessionId]);
  const showTurn = useCallback((turnKey: string) => {
    setSheetFor(null);
    const line = document.querySelector<HTMLElement>(
      `[data-probe-line="${CSS.escape(turnKey)}"]`,
    );
    if (line === null) return;
    const still = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    line.scrollIntoView({ block: "center", behavior: still ? "auto" : "smooth" });
    line.focus({ preventScroll: true });
  }, []);
  const stage = (
    <Stage
      call={call}
      onStart={begin}
      briefing={briefing}
      voice={voice}
      onVoiceChange={setVoice}
      mode={mode}
      onModeChange={setMode}
      orgName={orgName}
      disabled={organizationId === null || unconfigured || restricted}
      noOrganization={organizationId === null && !orgLoading}
      blocked={unconfigured || restricted}
      docked={hasTranscript}
      focus={focus}
      onClearFocus={() => onFocusChange(null)}
    />
  );

  return (
    <OpenRowsProvider onShow={showDesk}>
      <div className="@container/stage relative flex min-h-0 flex-1 flex-col" data-probe-stage="">
        <h1 className="sr-only">New interview</h1>
        {unconfigured || restricted ? (
          <div
            role="status"
            className="mx-auto mt-4 flex w-full max-w-xl items-start gap-2.5 rounded-lg border border-border/70 bg-muted/40 px-3.5 py-2.5 text-[13px] text-muted-foreground"
          >
            <TriangleAlertIcon aria-hidden className="mt-0.5 size-3.5 shrink-0 text-warning" />
            <span>
              {restricted ? (
                <>{RESTRICTED_PROVIDERS_COPY} Past interviews still open.</>
              ) : (
                <>
                  Calls can&rsquo;t start on this server: Probe&rsquo;s voice service isn&rsquo;t set
                  up. The briefing and past interviews still work.
                </>
              )}
            </span>
          </div>
        ) : null}
        <div
          className={cn(
            "flex min-h-0 flex-1 flex-col",
            deskShown && "@4xl/stage:grid @4xl/stage:grid-cols-[minmax(0,1fr)_340px]",
          )}
        >
          <div className="relative flex min-h-0 flex-1 flex-col">
            <CallCorners
              call={call}
              desk={
                deskShown ? (
                  <ProbeDeskButton
                    read={desk.read}
                    onClick={() => setSheetFor(state.sessionId)}
                    className="@4xl/stage:hidden"
                  />
                ) : null
              }
            />
            {hasTranscript ? (
              <>
                {stage}
                <div className="relative flex min-h-0 flex-1 flex-col">
                  <Conversation state={state} records={lineRecords} />
                  {/* The conversation fades into the prompt box the way it fades
                      under the examiner — no rule at either end. */}
                  <div
                    aria-hidden
                    className="pointer-events-none absolute inset-x-0 bottom-0 h-10 bg-linear-to-t from-background to-transparent"
                  />
                </div>
                {state.phase === "ended" && lastSaved !== null ? (
                  <SavedNotice orgSegment={area.orgSegment} interview={lastSaved} />
                ) : null}
              </>
            ) : (
              // Before the first word the examiner is the whole screen: the orb
              // sits at its centre. Once the interview starts it docks to the top
              // and the conversation takes the room.
              <div className="flex min-h-0 flex-1 flex-col items-center justify-center overflow-y-auto">
                {stage}
                <p className="mt-6 max-w-lg px-6 text-center text-sm text-muted-foreground text-balance">
                  {state.phase === "idle" || state.phase === "ended"
                    ? "Say what to rehearse, or let it open. It never changes a record."
                    : state.phase === "connecting"
                      ? "Connecting to the examiner…"
                      : state.phase === "error"
                        ? ""
                        : "Listening — say what you'd like to rehearse, or give it a moment and the examiner opens."}
                </p>
              </div>
            )}
            <div className="shrink-0 bg-background px-4 pt-1 pb-3 sm:px-6">
              <AskInText
                disabled={!canAsk}
                onAsk={call.ask}
                mic={call.active ? { muted: call.muted, onToggle: call.toggleMute } : null}
              />
            </div>
          </div>
          {deskShown ? (
            <aside
              ref={column}
              aria-label="The examiner's file"
              className="hidden min-h-0 flex-col border-s border-border/60 bg-card/40 @4xl/stage:flex"
            >
              {desk.render({ orgSegment: area.orgSegment, onShowTurn: showTurn })}
            </aside>
          ) : null}
        </div>
        {deskShown ? (
          <Sheet
            open={sheetFor === state.sessionId}
            onOpenChange={(open) => setSheetFor(open ? state.sessionId : null)}
          >
            <SheetPopup side="right" aria-labelledby={sheetHeadingId}>
              {desk.render({
                orgSegment: area.orgSegment,
                headingId: sheetHeadingId,
                onShowTurn: showTurn,
              })}
            </SheetPopup>
          </Sheet>
        ) : null}
      </div>
    </OpenRowsProvider>
  );
}

/** After a call: the interview is saved, and its debrief is one click away. */
function SavedNotice({
  orgSegment,
  interview,
}: {
  readonly orgSegment: string;
  readonly interview: ProbeInterviewSummary;
}) {
  return (
    <div className="shrink-0 px-4 sm:px-6">
      <div
        role="status"
        className="mx-auto flex w-full max-w-3xl items-center gap-3 rounded-lg border border-border/70 bg-card px-3.5 py-2.5 text-[13px] shadow-xs/5"
        data-probe-saved=""
      >
        <span className="min-w-0 flex-1 truncate text-muted-foreground">
          <span className="font-medium text-foreground">Interview saved.</span>{" "}
          {interview.debrief === null
            ? "The transcript is in your interviews."
            : "The examiner left a debrief."}
        </span>
        {interview.debrief !== null ? <OutcomeBadge outcome={interview.debrief.outcome} /> : null}
        <Button
          size="sm"
          variant="outline"
          render={
            <Link
              to="/$org/apps/probe/interviews/$sessionId"
              params={{ org: orgSegment, sessionId: interview.id }}
            />
          }
        >
          Open
          <ArrowRightIcon className="size-3.5" />
        </Button>
      </div>
    </div>
  );
}

/** The examiner: orb, what it is doing, and the call controls. */
function Stage({
  call,
  onStart,
  briefing,
  voice,
  onVoiceChange,
  mode,
  onModeChange,
  orgName,
  disabled,
  noOrganization,
  blocked,
  docked,
  focus,
  onClearFocus,
}: {
  readonly call: ProbeCall;
  /** Start the call — asking for the focus area, when one is picked. */
  readonly onStart: () => void;
  readonly focus: ProbeExaminationArea | null;
  readonly onClearFocus: () => void;
  readonly briefing: ProbeBriefing | null;
  readonly voice: ProbeVoice;
  readonly onVoiceChange: (voice: ProbeVoice) => void;
  readonly mode: ProbeMode;
  readonly onModeChange: (mode: ProbeMode) => void;
  readonly orgName: string | null;
  readonly disabled: boolean;
  readonly noOrganization: boolean;
  /** Calls can't start here; the notice above the stage says why. */
  readonly blocked: boolean;
  /** True once the conversation has begun and the stage sits at the top. */
  readonly docked: boolean;
}) {
  const { state } = call;
  const live = call.active;
  const busy = state.phase === "connecting";
  const status = describeProbePhase(state, call.activity);
  const notice = describeProbeNotice(state);
  const statusTone =
    state.phase === "error"
      ? "text-destructive"
      : state.phase === "live"
        ? "text-foreground"
        : "text-muted-foreground";
  const lastTool = state.tools.at(-1) ?? null;
  const running = state.tools.some((tool) => tool.status === "running");

  return (
    <section
      aria-label="Call"
      className={cn(
        "flex shrink-0 flex-col items-center px-6",
        // On a phone the call's corners span the width, so the docked orb starts below them.
        docked ? "pt-12 pb-5 sm:pt-5" : "py-4",
      )}
    >
      <button
        type="button"
        onClick={() => (live ? call.end() : onStart())}
        disabled={busy || disabled}
        aria-label={live ? "End the call" : "Start the interview"}
        className="group cursor-pointer rounded-full outline-none focus-visible:ring-2 focus-visible:ring-ring/40 focus-visible:ring-offset-4 focus-visible:ring-offset-background disabled:cursor-default"
      >
        {/* No colour prop: the orb is the theme's primary, and follows it. */}
        <FluidOrb
          size={docked ? DOCKED_ORB_SIZE : ORB_SIZE}
          activityRef={call.activityRef}
          className={cn(
            "shadow-[0_24px_60px_-28px_var(--primary)] transition-[opacity,transform] duration-300",
            !live && !busy && "opacity-90 group-hover:scale-[1.02] group-hover:opacity-100",
            busy && "motion-safe:animate-pulse",
          )}
        />
      </button>

      {focus !== null && !live ? (
        <div
          className="mt-5 inline-flex max-w-full items-center gap-1.5 rounded-full border border-border/70 bg-muted/50 py-0.5 ps-2.5 pe-1 text-xs text-foreground"
          data-probe-focus=""
        >
          <TargetIcon aria-hidden className="size-3.5 shrink-0 text-muted-foreground" />
          <span className="truncate">
            <span className="text-muted-foreground">Focus:</span> {areaLabel(focus)}
          </span>
          <button
            type="button"
            onClick={onClearFocus}
            aria-label="Clear the focus"
            className="inline-flex size-5 cursor-pointer items-center justify-center rounded-full text-muted-foreground outline-none transition-colors hover:bg-accent hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
          >
            <XIcon className="size-3" />
          </button>
        </div>
      ) : null}

      <div
        className={cn(
          "flex min-h-10 flex-col items-center justify-center gap-0.5 text-center",
          focus !== null && !live ? "mt-3" : docked ? "mt-3" : "mt-5",
        )}
      >
        <p className={cn("text-sm font-medium text-balance", statusTone)} aria-live="polite">
          {noOrganization
            ? "Choose an organization to start."
            : blocked && !live
              ? "Calls can't start here."
              : status}
        </p>
        {notice !== null ? (
          <p
            role="status"
            className="inline-flex max-w-lg items-start gap-1.5 text-xs text-foreground text-balance"
            data-probe-notice=""
          >
            <TriangleAlertIcon aria-hidden className="mt-px size-3.5 shrink-0 text-warning" />
            {notice}
          </p>
        ) : null}
        {live ? (
          lastTool !== null && !running ? (
            <p className={cn("text-xs", DESK_LABEL_CLASS)}>
              Last read: {describeToolName(lastTool.name)}
            </p>
          ) : null
        ) : (
          <p className="text-xs text-muted-foreground">{briefingLine(briefing, orgName)}</p>
        )}
      </div>

      {live ? null : (
        <div
          className={cn("flex flex-wrap items-center justify-center gap-2", docked ? "mt-3" : "mt-4")}
        >
            <Button
              type="button"
              size="sm"
              onClick={onStart}
              disabled={busy || disabled}
              className="cursor-pointer"
            >
              <PhoneIcon className="size-3.5" />
              {state.phase === "ended" || state.phase === "error"
                ? "Start again"
                : "Start the interview"}
            </Button>
            <ModePicker mode={mode} onChange={onModeChange} disabled={busy} />
            <ProbeVoicePicker voice={voice} onChange={onVoiceChange} disabled={busy} />
        </div>
      )}
    </section>
  );
}

/** Who the examiner has read up on, before the call — or what it is, without a briefing. */
const briefingLine = (briefing: ProbeBriefing | null, orgName: string | null): string => {
  if (briefing === null) {
    return `The examiner from ${orgName ?? "your organization"}’s regulator. It asks; you answer.`;
  }
  const who = briefing.person.name ?? briefing.person.email;
  const role = briefing.person.jobTitle ? `, ${briefing.person.jobTitle}` : "";
  const past =
    briefing.rehearsals === 0
      ? "your first rehearsal"
      : `${briefing.rehearsals} past rehearsal${briefing.rehearsals === 1 ? "" : "s"} remembered`;
  return `Briefed on ${who}${role} — ${past}.`;
};

const PROBE_MODE_OPTIONS: ReadonlyArray<{
  readonly value: ProbeMode;
  readonly name: string;
  readonly description: string;
}> = [
  { value: "guided", name: "Guided", description: "Feedback after each answer" },
  { value: "real", name: "Real interview", description: "Feedback when you say you’ve finished" },
];

/**
 * Guided coaching or a real interview. Like the voice, a session's mode is
 * fixed when it starts, so the picker lives beside Start.
 */
function ModePicker({
  mode,
  onChange,
  disabled,
}: {
  readonly mode: ProbeMode;
  readonly onChange: (mode: ProbeMode) => void;
  readonly disabled: boolean;
}) {
  const items = useMemo(
    () => PROBE_MODE_OPTIONS.map((option) => ({ value: option.value, label: option.name })),
    [],
  );
  return (
    <Select
      modal={false}
      value={mode}
      onValueChange={(value) => onChange(value as ProbeMode)}
      items={items}
      disabled={disabled}
    >
      <SelectTrigger variant="ghost" size="sm" className="font-normal" aria-label="Interview mode">
        <span className="text-muted-foreground">Mode</span>
        <SelectValue />
      </SelectTrigger>
      <SelectPopup>
        <SelectGroup>
          <SelectGroupLabel>Interview mode</SelectGroupLabel>
          {PROBE_MODE_OPTIONS.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              <span className="flex flex-col">
                <span>{option.name}</span>
                <span className="text-xs text-muted-foreground">{option.description}</span>
              </span>
            </SelectItem>
          ))}
        </SelectGroup>
      </SelectPopup>
    </Select>
  );
}

/**
 * The interview in text, in the chat's own clothes: your answers as the
 * chat's message bubbles, the examiner's questions as markdown. Sticks to
 * the end while new turns arrive unless you have scrolled up to re-read.
 */
/** A line the desk took the reader to, until focus moves on. */
const LINE_SHOWN_CLASS =
  "rounded-xl outline-none focus:ring-2 focus:ring-ring/50 focus:ring-offset-4 focus:ring-offset-background";

function Conversation({
  state,
  records,
}: {
  readonly state: ProbeConversationState;
  /** What the examiner read for each line, by the line's id (`lineRecordsOf`). */
  readonly records: ReadonlyMap<string, ReadonlyArray<DeskRecord>>;
}) {
  const scroller = useRef<HTMLDivElement>(null);
  const stick = useRef(true);
  const items = useMemo(() => groupTranscript(state.transcript), [state.transcript]);

  const onScroll = () => {
    const node = scroller.current;
    if (!node) return;
    stick.current = node.scrollHeight - node.scrollTop - node.clientHeight < 48;
  };

  useEffect(() => {
    const node = scroller.current;
    if (!node || !stick.current) return;
    node.scrollTo({ top: node.scrollHeight });
  }, [state.transcript, records]);

  return (
    <div
      ref={scroller}
      onScroll={onScroll}
      // The chat's own top fade instead of a rule: the conversation slips
      // under the examiner rather than stopping at a line.
      className="chat-timeline-scroll-fade min-h-0 flex-1 overflow-y-auto overscroll-contain"
      aria-label="Interview"
    >
      <div className="mx-auto flex w-full max-w-3xl flex-col gap-5 px-4 pt-10 pb-6 sm:px-6 sm:pt-12">
        {items.map((item, index) =>
          item.kind === "answer" ? (
            <div
              key={item.entry.id}
              tabIndex={-1}
              data-probe-line={item.entry.id}
              className={cn("group flex flex-col items-end gap-1", LINE_SHOWN_CLASS)}
            >
              <div className="relative max-w-[80%] rounded-2xl bg-message p-3 text-message-foreground">
                <p className="whitespace-pre-wrap text-sm leading-relaxed">
                  {item.entry.text.trim()}
                </p>
              </div>
              {item.backchannels.length > 0 && (
                // What the examiner murmured while you spoke — heard, not a turn.
                <p
                  className="pe-3 text-xs italic text-muted-foreground"
                  aria-label="The examiner, listening"
                >
                  {item.backchannels.join(" · ")}
                </p>
              )}
              {records.has(item.entry.id) && <ProbeRecordChips records={records.get(item.entry.id)!} />}
            </div>
          ) : (
            <div
              key={item.entry.id}
              tabIndex={-1}
              data-probe-line={item.entry.id}
              className={cn("group/assistant relative min-w-0 px-1 py-0.5", LINE_SHOWN_CLASS)}
            >
              <ChatMarkdown
                text={item.entry.text.trim()}
                cwd={undefined}
                isStreaming={index === items.length - 1 && state.phase === "live"}
                lineBreaks
              />
              {records.has(item.entry.id) && <ProbeRecordChips records={records.get(item.entry.id)!} />}
            </div>
          ),
        )}
      </div>
    </div>
  );
}

/**
 * The live call's two controls, in the call area's top end corner: Restart
 * as an icon, and End interview named in full. Below the desk column's width, the examiner's file
 * opens from the other corner; on a phone the docked orb sits below both.
 */
function CallCorners({ call, desk }: { readonly call: ProbeCall; readonly desk: ReactNode }) {
  return (
    <>
      {desk !== null ? <div className="absolute start-3 top-3 z-10 sm:start-4">{desk}</div> : null}
      {call.active ? (
        <div
          className="absolute end-3 top-3 z-10 flex items-center gap-1.5 sm:end-4"
          data-probe-call-corner=""
        >
          <IconAction label="Restart" onClick={call.restart} variant="ghost" data-probe-restart="">
            <RotateCcwIcon />
          </IconAction>
          <Button
            type="button"
            variant="destructive"
            size="sm"
            onClick={call.end}
            className="cursor-pointer"
            data-probe-end=""
          >
            <PhoneOffIcon />
            End interview
          </Button>
        </div>
      ) : null}
    </>
  );
}

/** An icon-only button named by its label, which its tooltip shows. */
function IconAction({
  label,
  tooltip = label,
  onClick,
  variant,
  pressed,
  children,
  className,
  ...rest
}: {
  readonly label: string;
  readonly tooltip?: string;
  readonly onClick: () => void;
  readonly variant: "ghost" | "destructive";
  readonly pressed?: boolean;
  readonly children: ReactNode;
  readonly className?: string;
} & Record<`data-${string}`, string>) {
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Button
            type="button"
            variant={variant}
            size="icon-sm"
            aria-label={label}
            aria-pressed={pressed}
            onClick={onClick}
            className={cn("cursor-pointer", variant === "ghost" && "text-muted-foreground", className)}
            {...rest}
          />
        }
      >
        {children}
      </TooltipTrigger>
      <TooltipPopup>{tooltip}</TooltipPopup>
    </Tooltip>
  );
}

/** For the answers you would rather type; the examiner still speaks. */
function AskInText({
  disabled,
  onAsk,
  mic,
}: {
  readonly disabled: boolean;
  readonly onAsk: (text: string) => void;
  /** The microphone, while a call is live. */
  readonly mic: { readonly muted: boolean; readonly onToggle: () => void } | null;
}) {
  const [text, setText] = useState("");
  const submit = (event: FormEvent) => {
    event.preventDefault();
    const trimmed = text.trim();
    if (trimmed.length === 0) return;
    onAsk(trimmed);
    setText("");
  };
  return (
    <form onSubmit={submit} className="mx-auto flex w-full max-w-3xl items-center gap-2">
      {mic !== null ? (
        <IconAction
          label="Mute"
          tooltip={mic.muted ? "Unmute" : "Mute"}
          pressed={mic.muted}
          onClick={mic.onToggle}
          variant="ghost"
          className={cn(mic.muted && "bg-destructive/10 text-destructive-foreground hover:bg-destructive/15")}
          data-probe-mute=""
        >
          {mic.muted ? <MicOffIcon /> : <MicIcon />}
        </IconAction>
      ) : null}
      <Input
        value={text}
        onChange={(event) => setText(event.target.value)}
        disabled={disabled}
        aria-label="Answer in text"
        placeholder="Answer in text"
        className="flex-1"
      />
      <Button
        type="submit"
        size="icon-sm"
        variant={disabled || text.trim().length === 0 ? "outline" : "default"}
        disabled={disabled || text.trim().length === 0}
        aria-label="Send"
        className="cursor-pointer"
      >
        <ArrowUpIcon className="size-3.5" />
      </Button>
    </form>
  );
}
